import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';
import { getLastPrice, getHostName, formatCurrency, isPriceNotListed } from '../../../utils/Utils';
import { ProductService } from '../../../service/ProductService';
import { pricePosition } from './productSignals';

// Price-position chip label/color. Must cover every level pricePosition can return
// ('average' included), and uses the same taxonomy and colors as the status column.
const POSITION_LABEL = {
  highest: { key: 'product.myprice_highest', cls: 'pux-chip-red' },
  high: { key: 'product.myprice_avg_higher', cls: 'pux-chip-orange' },
  average: { key: 'product.myprice_average', cls: 'pux-chip-yellow' },
  low: { key: 'product.myprice_avg_lower', cls: 'pux-chip-cyan' },
  same: { key: 'product.myprice_same', cls: 'pux-chip-blue' },
  lowest: { key: 'product.myprice_lowest', cls: 'pux-chip-green' },
};

// Per-seller competitor breakdown for competitive/dropshipping/market rows.
// The competitive list payload does NOT include competitors (fetch_competitors=False
// for performance), so we lazy-load them on expand via getCompetitors.
// `showMine` (competitive/dropshipping): show a reference banner with my own listing
// (is_mine) price + stock, and compare competitors against it.
export default function CompetitorRows({ row, t, showComparison = true, highlightMine = false }) {
  const showMine = highlightMine;
  const [competitors, setCompetitors] = useState(null); // null = loading
  const [visible, setVisible] = useState(10);

  useEffect(() => {
    let active = true;
    const end = new Date();
    const start = moment(end).subtract(7, 'days').toDate();
    new ProductService().getCompetitors(row.id, start, end)
      .then(c => { if (active) setCompetitors(c || []); })
      .catch(() => { if (active) setCompetitors([]); });
    return () => { active = false; };
  }, [row.id]);

  if (competitors === null) {
    return <div style={{ padding: 12, textAlign: 'center' }}><ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="4" /></div>;
  }

  const priceOf = (s) => {
    const lp = getLastPrice(s.prices);
    return lp && lp.value != null ? lp.value : (s.price != null ? s.price : null);
  };
  const nameOf = (s) => (s.name && s.name !== '' ? s.name : getHostName(s.product_url));

  // My own listing (is_mine) goes in the reference banner, not the competitor table.
  const mine = competitors.find(s => s.is_mine);
  const myPrice = mine ? priceOf(mine) : (row.price != null ? row.price : null);
  const myLp = mine ? getLastPrice(mine.prices) : null;
  const myInStock = myLp && myLp.stock != null ? myLp.stock > 0 : null;

  // In-stock competitors only, cheapest first. A seller with no listed price sells the
  // product too, so it is shown, behind the priced ones.
  // My own listing (is_mine) is shown in the reference banner, not the table.
  const inStock = (s) => { const lp = getLastPrice(s.prices); return !!(lp && lp.stock > 0); };
  const notListed = (s) => isPriceNotListed(getLastPrice(s.prices));
  const sorted = competitors.filter(s => !s.is_mine && inStock(s))
    .sort((a, b) => (notListed(a) - notListed(b)) || (priceOf(a) - priceOf(b)));

  const vsMine = (s) => {
    const p = priceOf(s);
    if (p == null || myPrice == null || notListed(s)) return null;
    if (p < myPrice) return <span className="pux-chip pux-chip-amber">{t('product.cheaper_than_me')}</span>;
    if (p > myPrice) return <span className="pux-chip pux-chip-green">{t('product.pricier_than_me')}</span>;
    return <span className="pux-chip pux-chip-gray">{t('product.myprice_same')}</span>;
  };
  const showVs = showComparison && myPrice != null && myPrice > 0;

  const pos = pricePosition(row);
  const posLabel = pos ? POSITION_LABEL[pos.level] : null;
  const myBand = showMine && (
    <div className="pux-maprow">
      <i className="pi pi-bolt" />
      <span>{t('product.my_price')}: <b style={{ color: myPrice != null ? 'inherit' : '#9ca3af' }}>
        {myPrice != null ? formatCurrency(myPrice, row.currency) : t('product.not_set')}
      </b></span>
      {myInStock != null && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span className="pux-dot" style={{ background: myInStock ? '#16a34a' : '#dc2626' }} />
          {t(myInStock ? 'product.stock_in' : 'product.stock_out')}
        </span>
      )}
      {posLabel && <span className={`pux-chip ${posLabel.cls}`}>{t(posLabel.key)}</span>}
      {row.position != null && <span>{t('product.position')}: <b>{row.position}{row.competitorsCount != null ? ` / ${row.competitorsCount + 1}` : ''}</b></span>}
    </div>
  );

  if (!sorted.length) {
    return <div>{myBand}<div style={{ color: '#6b7280', padding: 8 }}>{t('product.no_violator_detail')}</div></div>;
  }
  return (
    <div>
      {myBand}
      <DataTable value={sorted.slice(0, visible)} responsiveLayout="scroll" className="p-datatable-sm">
        <Column body={(s) => s.product_url
          ? <a href={s.product_url} target="_blank" rel="noreferrer">{nameOf(s)}</a>
          : nameOf(s)} header={t('product.seller')} />
        <Column body={(s) => notListed(s) ? t('product_detail.no_price_listed') : formatCurrency(priceOf(s), row.currency)} header={t('product.price')} />
        {showVs && <Column body={vsMine} header={t('product.status')} />}
      </DataTable>
      {sorted.length > visible && (
        <button type="button" className="pux-loadmore" onClick={() => setVisible(v => v + 20)}>
          {t('product.load_more', { count: sorted.length - visible })}
        </button>
      )}
    </div>
  );
}
